import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import styled from 'styled-components';

const ProfileImage = styled.img`
  width: 150px;
  height: 150px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid white;
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
`;

const ServiceImage = styled.img`
  width: 100px;
  height: 100px;
  object-fit: cover;
  border-radius: 4px;
  margin: 8px;
`;

const ImageUploadContainer = styled(Box)`
  border: 2px dashed #90caf9;
  padding: 20px;
  text-align: center;
  border-radius: 8px;
  cursor: pointer;
  background: #f5faff;
  &:hover {
    border-color: #2196f3;
    background: #e3f2fd;
  }
`;

const ProfessionalDashboard = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    profession: '',
    description: '',
    contact: '',
    profileImage: null,
    serviceImages: [],
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await fetch('/api/providers/me');
        if (!response.ok) throw new Error('Erro ao carregar perfil');
        const data = await response.json();
        setProfile(data);
        setFormData({
          name: data.name || '',
          profession: data.profession || '',
          description: data.description || '',
          contact: data.contact || '',
          profileImage: null,
          serviceImages: data.serviceImages || [],
        });
      } catch (error) {
        setError('Erro ao carregar perfil');
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const handleImageUpload = (event, type) => {
    const file = event.target.files[0];
    if (type === 'profile') {
      setFormData({ ...formData, profileImage: file });
    } else {
      if (formData.serviceImages.length < 10) {
        setFormData({
          ...formData,
          serviceImages: [...formData.serviceImages, file],
        });
      }
    }
  };

  const removeServiceImage = (index) => {
    const newImages = formData.serviceImages.filter((_, i) => i !== index);
    setFormData({ ...formData, serviceImages: newImages });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'serviceImages') {
          formData.serviceImages.forEach(image => {
            formDataToSend.append('serviceImages', image);
          });
        } else if (key === 'profileImage' && formData.profileImage) {
          formDataToSend.append('profileImage', formData.profileImage);
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });

      const response = await fetch('/api/providers/me', {
        method: 'PUT',
        body: formDataToSend,
      });

      if (!response.ok) throw new Error('Erro ao atualizar perfil');
      
      setSuccess('Perfil atualizado com sucesso!');
    } catch (error) {
      setError('Erro ao atualizar perfil');
    }
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ maxWidth: 800, mx: 'auto', mt: 5, mb: 5 }}>
        <Typography variant="h4" gutterBottom align="center" color="primary">
          Meu Perfil Profissional
        </Typography>

        <Card sx={{ borderRadius: 4, boxShadow: 6, background: '#fafdff' }}>
          <CardContent>
            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} display="flex" justifyContent="center">
                  <Box textAlign="center">
                    <ProfileImage
                      src={formData.profileImage ? URL.createObjectURL(formData.profileImage) : (profile?.profileImage || '/default-profile.png')}
                      alt="Foto de perfil"
                    />
                    <Box mt={2}>
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        id="profile-image"
                        onChange={(e) => handleImageUpload(e, 'profile')}
                      />
                      <label htmlFor="profile-image">
                        <Button
                          component="span"
                          startIcon={<CloudUploadIcon />}
                          variant="outlined"
                          color="primary"
                        >
                          Alterar Foto
                        </Button>
                      </label>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nome"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Profissão"
                    value={formData.profession}
                    onChange={(e) => setFormData({ ...formData, profession: e.target.value })}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Contato (WhatsApp)"
                    value={formData.contact}
                    onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                    required
                    helperText="Digite apenas números"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Descrição"
                    multiline
                    rows={4}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    helperText="Fale sobre sua experiência, especialidades e diferenciais"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Fotos dos Trabalhos
                  </Typography>
                  <ImageUploadContainer>
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      id="service-images"
                      onChange={(e) => handleImageUpload(e, 'service')}
                    />
                    <label htmlFor="service-images">
                      <Button
                        component="span"
                        startIcon={<CloudUploadIcon />}
                        variant="outlined"
                        color="primary"
                        disabled={formData.serviceImages.length >= 10}
                      >
                        Adicionar Fotos
                      </Button>
                    </label>
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      Máximo de 10 fotos
                    </Typography>
                  </ImageUploadContainer>

                  <Box display="flex" flexWrap="wrap" mt={2}>
                    {formData.serviceImages.map((image, index) => (
                      <Box key={index} position="relative">
                        <ServiceImage
                          src={URL.createObjectURL(image)}
                          alt={`Trabalho ${index + 1}`}
                        />
                        <IconButton
                          size="small"
                          onClick={() => removeServiceImage(index)}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            bgcolor: 'rgba(255,255,255,0.8)',
                            '&:hover': {
                              bgcolor: 'rgba(255,255,255,0.9)',
                            },
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    fullWidth
                  >
                    Salvar Alterações
                  </Button>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default ProfessionalDashboard; 